<template>
  <div>
    <edit-h1-parcel-items
      v-if="selectedParcel && declarationType === 'h1'"
      id="edit-h1-parcel-items"
      :declaration="selectedDeclaration"
      :parcel="selectedParcel"
      :shipment="shipment"
      :readonly="true"
    />
    <edit-parcel-items
      v-if="selectedParcel && declarationType !== 'h1'"
      id="edit-parcel-items"
      :parcel="selectedParcel"
      :declaration="selectedDeclaration"
      :shipment="shipment"
      :readonly="true"
    />
    <b-row>
      <b-col
        class="mt-2"
        cols="9"
      >
        <b-card-text v-if="preparingToSend">
          Parcels that are prepared to send to customs.
        </b-card-text>
        <b-card-text v-else>
          Parcels that are waiting for customs response.
        </b-card-text>
      </b-col>
    </b-row>
    <b-row>
      <b-col>
        <div class="overflow-x-scroll">
          <b-table
            :items="declarations"
            responsive
            :fields="fields"
            :busy="loading"
            :sort-by.sync="sortBy"
            :sort-desc.sync="isSortDirDesc"
            no-local-sorting
            show-empty
            empty-text="No matching records found"
            class="mb-0"
          >
            <template #table-busy>
              <div class="text-center my-2">
                <b-spinner class="align-middle mr-1" />
                <strong>Loading...</strong>
                <p
                  v-if="timeout > 0"
                  class="mt-1"
                >
                  It seems that declarations are actively changing status. List will be update with delay.
                </p>
              </div>
            </template>
            <template #head()="data">
              <span class="text-nowrap">
                {{ data.label }}
              </span>
            </template>

            <template #cell(actions)="data">
              <b-button
                type="button"
                class="parcel_view"
                variant="primary"
                size="sm"
                @click="openParcelModal(data.item)"
              >
                <b-icon-eye />
              </b-button>
            </template>

            <template #cell(tracking_number)="data">
              {{ (data.item.goods_shipment || data.item.parcel || {}).tracking_number }}
            </template>

            <template #cell(container_code)="data">
              {{ (data.item.goods_shipment || data.item.parcel || {}).container_code }}
            </template>

            <template #cell(updated_at)="data">
              {{ data.item.updated_at | formatDate }}
            </template>

          </b-table>
        </div>
        <pagination
          v-show="!loading"
          :per-page="perPage"
        />
      </b-col>
    </b-row>
  </div>
</template>

<script>
import {
  BCardText,
  BCol,
  BRow,
  BTable,
  BSpinner,
  BButton,
  BIconEye,
} from 'bootstrap-vue'
import Pagination from '@/layouts/components/Pagination/Pagination.vue'
import EditParcelItems from '@/views/shipments/declarations/modals/EditParcelItems.vue'
import EditH1GoodsItems from '@/views/shipments/declarations/modals/EditH1GoodsItems.vue'
import { EventBus } from '@core/utils/utils'
import axios from 'axios'
import { searchQuery } from '@core/utils/filter'

export default {
  components: {
    BButton,
    BIconEye,
    EditParcelItems,
    EditH1ParcelItems: EditH1GoodsItems,
    BCardText,
    BCol,
    BRow,
    BTable,
    BSpinner,
    Pagination,
  },
  props: {
    shipment: {
      type: Object,
      required: true,
    },
    preparingToSend: {
      type: Boolean,
      default: false,
    },
    declarationType: {
      type: String,
      default: 'h7',
    },
    filters: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      timeout: -1,
      loading: false,
      page: 1,
      perPage: 100,
      selectedDeclaration: null,
      selectedParcel: null,
      sortBy: null,
      isSortDirDesc: null,
      fields: [
        { key: 'actions', label: 'Actions' },
        { key: 'tracking_number', label: 'Tracking code', sortable: true },
        { key: 'container_code', label: 'Container', sortable: true },
        { key: 'lrn', label: 'LRN' },
        { key: 'mrn', label: 'MRN' },
        { key: 'updated_at', label: 'Declaration time' },
      ],
      declarations: [],
      intervalId: null,
      requestCanceller: null,
    }
  },
  computed: {
    url() {
      let url = `/v1/companies/${this.$activeCompany().data.company.id}/shipments/${this.shipment.id}/declarations/${this.declarationType}/waiting-for-customs`
      if (this.preparingToSend) {
        url = `/v1/shipments/${this.shipment.id}/declarations/${this.declarationType}/preparing-to-send`
      }
      url += `?page=${this.page}`
      url += `&per_page=${this.perPage}`
      return url
    },
  },
  watch: {
    filters() {
      this.fetchData()
    },
    sortBy() {
      this.fetchData()
    },
    isSortDirDesc() {
      this.fetchData()
    },
  },
  created() {
    EventBus.$on('update-parcel-items', this.handleUpdateParcelItems)
    EventBus.$on('general-declaration-update', this.handleGeneralDeclarationUpdate)

    EventBus.$on('pageChange', this.handlePageChange)
    EventBus.$on('renewDeclarationList', this.handleRenewDeclarationList)
    this.fetchData()
    this.startTicker()
  },
  beforeDestroy() {
    EventBus.$off('update-parcel-items', this.handleUpdateParcelItems)
    EventBus.$off('general-declaration-update', this.handleGeneralDeclarationUpdate)

    EventBus.$off('pageChange', this.handlePageChange)
    EventBus.$off('renewDeclarationList', this.handleRenewDeclarationList)
  },
  methods: {
    handlePageChange(newPage) {
      this.page = newPage
      this.fetchData()
    },
    handleUpdateParcelItems() {
      this.selectedDeclaration = null
      this.selectedParcel = null
      this.fetchData()
    },
    handleGeneralDeclarationUpdate() {
      if (!this.intervalId) {
        this.startTicker()
      }
      this.timeout = 6
      if (this.requestCanceller !== null) {
        this.requestCanceller.cancel('Request has been canceled')
      }
      this.fetchData()
    },
    handleRenewDeclarationList() {
      this.fetchData()
      EventBus.$emit('refresh-statistics')
    },
    startTicker() {
      this.intervalId = setInterval(this.countDown, 1000)
    },
    countDown() {
      this.timeout -= 1

      if (this.timeout > 0) {
        this.loading = true
      }

      if (this.timeout === 0) {
        this.fetchData()
      }
    },
    openParcelModal(declaration) {
      this.selectedParcel = declaration.goods_shipment || {}
      this.selectedDeclaration = declaration
      if (this.declarationType === 'h1') {
        this.$nextTick(() => {
          this.$bvModal.show('edit-h1-parcel-items')
        })
      } else {
        this.$nextTick(() => {
          this.$bvModal.show('edit-parcel-items')
        })
      }
    },
    fetchData(activeTab, showLoader = true) {
      if (
        (
          !activeTab
          || (activeTab.startsWith('waiting-customs') && !this.preparingToSend)
          || (activeTab.startsWith('preparing-to-send') && this.preparingToSend)
        )
        && !this.loading
      ) {
        this.requestCanceller = axios.CancelToken.source()
        if (showLoader) {
          this.loading = true
        }
        this.$http.get(searchQuery(this.url, this.filters), {
          params: {
            sortBy: this.sortBy,
            sortDesc: this.isSortDirDesc,
          },
          cancelToken: this.requestCanceller.token,
        })
          .then(response => {
            this.loading = false
            this.declarations = response.data.data
            EventBus.$emit('updateMeta', response.data.meta)
          })
      }
    },
  },
}
</script>
